import { FC } from "react";

import { theme } from "@ovou-inc/ovou-ui";

import { isCloseToWhite } from "utils/colorUtils";

export interface IShimmerProps {
  primaryColor: string;
  secondaryColor: string;
  primaryHeadColor: string;
  primarySubtitleColor: string;
  secondaryHeadColor: string;
  width: string;
  border: string;
}

/**
 * A live mini-card preview shown beside the color picker — renders the picked
 * primary (card background, hex or gradient) and secondary (CTA) colors.
 * Ported from front-v2's ProfileCardShimmer.
 */
export const Shimmer: FC<IShimmerProps> = ({
  primaryColor,
  secondaryColor,
  primaryHeadColor,
  primarySubtitleColor,
  secondaryHeadColor,
  width,
  border,
}) => {
  return (
    <svg
      width={width}
      viewBox="0 0 284 349"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        {primaryColor.includes("linear-gradient") &&
          (() => {
            // Parse `linear-gradient(<angle>, <stop>...)` robustly — stops may be
            // hex, rgb(a) or hsl(a), so we can't split on ", " (rgba/hsl contain
            // it). Extract the angle, then match each color + optional offset.
            const inner = primaryColor.slice(
              primaryColor.indexOf("(") + 1,
              primaryColor.lastIndexOf(")"),
            );
            const angleMatch = inner.match(/^\s*(-?\d+(?:\.\d+)?)deg/);
            const angle = angleMatch ? Number(angleMatch[1]) : 180;
            const stopRegex =
              /(#[0-9a-fA-F]{3,8}|rgba?\([^)]*\)|hsla?\([^)]*\))\s*(\d+(?:\.\d+)?%)?/g;
            const stops: { color: string; offset?: string }[] = [];
            let match: RegExpExecArray | null;
            while ((match = stopRegex.exec(inner)) !== null) {
              stops.push({ color: match[1], offset: match[2] });
            }
            return (
              <linearGradient
                gradientTransform={`rotate(${190 - angle})`}
                id="gradient"
              >
                {stops.map((stop, index) => (
                  <stop
                    key={index}
                    stopColor={stop.color}
                    offset={stop.offset}
                  />
                ))}
              </linearGradient>
            );
          })()}
      </defs>
      <path
        d="M4 10C4 5.58173 7.58173 2 12 2H272C276.418 2 280 5.58172 280 10V347H4V10Z"
        stroke="url(#paint0_linear_6_21944)"
        strokeWidth="4"
      />
      <path
        d="M6 10C6 6.68629 8.68629 4 12 4H272C275.314 4 278 6.68629 278 10V135H6V10Z"
        fill={secondaryColor}
      />
      <rect
        x="172.5"
        y="18.5"
        width="88"
        height="28"
        rx="7.5"
        stroke={secondaryHeadColor}
        strokeOpacity="0.3"
      />
      <g filter="url(#filter0_d_6_21944)">
        <rect
          x="20.8193"
          y="58.1909"
          width="242.852"
          height="152.225"
          rx="10"
          fill={
            primaryColor.includes("linear") ? "url(#gradient)" : primaryColor
          }
        />
      </g>
      <path
        d="M20.8193 68.1909C20.8193 62.6681 25.2964 58.1909 30.8193 58.1909H129.855V210.416H30.8193C25.2964 210.416 20.8193 205.939 20.8193 200.416V68.1909Z"
        fill="#292929"
      />
      <path
        opacity="0.4"
        d="M75.3374 153.066C88.2407 153.066 98.7018 140.545 98.7018 125.099C98.7018 109.653 95.2673 97.1321 75.3374 97.1321C55.4075 97.1321 51.9723 109.653 51.9723 125.099C51.9723 140.545 62.4334 153.066 75.3374 153.066Z"
        fill={theme.palette.ui.greys.grey1}
      />
      <path
        opacity="0.4"
        d="M118.478 194.946C118.055 167.815 114.565 160.084 87.8592 155.19C87.8592 155.19 84.1 160.054 75.338 160.054C66.576 160.054 62.8162 155.19 62.8162 155.19C36.4021 160.03 32.7 167.647 32.2132 194.064C32.1733 196.222 32.1548 196.335 32.1476 196.085C32.1493 196.554 32.2132 210.416 32.2132 210.416H75.337H118.526C118.526 210.416 118.525 197.287 118.526 196.827C118.519 196.981 118.505 196.681 118.478 194.946Z"
        fill={theme.palette.ui.greys.grey1}
      />
      <rect
        x="20.5"
        y="227"
        width="242"
        height="40"
        rx="10"
        fill={secondaryColor}
        stroke={
          isCloseToWhite(secondaryColor) ? theme.palette.ui.greys.grey3 : "none"
        }
        strokeWidth={isCloseToWhite(secondaryColor) ? 1 : 0}
      />
      <path
        d="M180 247H105"
        stroke={secondaryHeadColor}
        strokeWidth="4"
        strokeLinecap="round"
      />
      <path
        d="M237 106H150"
        stroke={primaryHeadColor}
        strokeWidth="10"
        strokeLinecap="round"
      />
      <path
        d="M84 32H23"
        stroke={secondaryHeadColor}
        strokeWidth="5"
        strokeLinecap="round"
      />
      <path
        d="M244 32H193"
        stroke={secondaryHeadColor}
        strokeWidth="5"
        strokeLinecap="round"
      />
      <path
        d="M211 127H150"
        stroke={primaryHeadColor}
        strokeWidth="10"
        strokeLinecap="round"
      />
      <path
        d="M182 162H148"
        stroke={primarySubtitleColor}
        strokeWidth="5"
        strokeLinecap="round"
      />
      <path
        d="M209 175H148"
        stroke={primarySubtitleColor}
        strokeWidth="5"
        strokeLinecap="round"
      />
      <path
        d="M254 330H25"
        stroke={theme.palette.ui.greys.grey3}
        strokeWidth="5"
        strokeLinecap="round"
      />
      <path
        d="M208 346H25"
        stroke={theme.palette.ui.greys.grey3}
        strokeWidth="5"
        strokeLinecap="round"
      />
      <path
        d="M88 305H27"
        stroke={theme.palette.ui.greys.grey3}
        strokeWidth="10"
        strokeLinecap="round"
      />
      <defs>
        <filter
          id="filter0_d_6_21944"
          x="0.819275"
          y="44.1909"
          width="282.852"
          height="192.225"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="6" />
          <feGaussianBlur stdDeviation="10" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.12 0"
          />
          <feBlend
            mode="normal"
            in2="BackgroundImageFix"
            result="effect1_dropShadow_6_21944"
          />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_dropShadow_6_21944"
            result="shape"
          />
        </filter>
        <linearGradient
          id="paint0_linear_6_21944"
          x1="142"
          y1="91.5"
          x2="142"
          y2="349"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={border} />
          <stop offset="1" stopColor={border} stopOpacity="0" />
        </linearGradient>
      </defs>
    </svg>
  );
};
